import * as yup from 'yup';
import {
	Row,
	RenderConfig,
	Column,
	TextInput,
	EMPTY_ARRAY,
	NumberInput,
	SelectInput,
	BOOLEAN_OPTIONS,
	EMPTY_STRING,
	SearchSelectInput,
	AsyncSelect
} from 'commons-ui';
import _sumBy from 'lodash/sumBy';
import _sum from 'lodash/sum';
import _get from 'lodash/get';
import {
	AB_TEST_BUCKET_DESC_MIN_LENGTH,
	AB_TEST_DESC_MIN_LENGTH,
	BUCKET_DESC_MIN_LENGTH_ERROR_MESSAGE,
	METRICS_OPTIONS,
	TOTAL_BUCKET_SHARE
} from './constants';

export const FORM_FIELD_KEYS = {
	NAME: 'name',
	START: 'start',
	END: 'end',
	DESC: 'description',
	FLOW_OR_SCREENS_IMPACTED: 'flowOrScreensImpacted',
	CONTROL_BUCKET: 'controlBucket',
	CONTROL_BUCKET_SHARE: 'controlBucketShare',
	TEST_BUCKETS: 'testBuckets',
	BUCKET_SHARE: 'bucketShare',
	BUCKET_NAME: 'bucketName',
	BUCKET_DESC: 'bucketDesc',
	IS_ALLOWED: 'isAllowed',
	SEGMENT_ID: 'segmentId',
	METRICS: 'metrics',
	BUCKET_TRANSITION_DATA: 'bucketTransitionData',
	FROM: 'from',
	TO: 'to',
	PERCENTAGE: 'percentage',
	USE_OLD_AB_FLOW: 'useOldAbFlow',
	RECENCY: 'recency',
	SEND_AB_TEST_DATA_ON_CT: 'sendAbtestDataOnCT'
};
export const startDateDependencyResolver = (props: any) => {
	const initialStart = _get(props, 'initialValues.start', Date.now());
	const initialEnd = _get(props, 'initialValues.end', Date.now());
	const isEditable = props.isEditable[props.mode];

	if (isEditable && initialStart < Date.now() && initialEnd > Date.now()) {
		return { disabled: true };
	}

	return { disabled: false };
};

export const controlShareDependencyResolver = (props: any, dependencyValues: any) => {
	const { name, setValue } = props;
	const testBucketShareSum = _sum(dependencyValues);
	setValue(name, 100 - testBucketShareSum);
};

export const validationSchema = yup.object().shape({
	name: yup
		.string()
		.required('Name is a required field')
		.min(1, 'Name cannot be empty')
		.matches(/^[^-]*$/, 'Name should not contain hyphens')
		.transform((value) => value.trim().replace(/ /g, EMPTY_STRING)),
	description: yup
		.string()
		.required('Description is a required field')
		.min(AB_TEST_DESC_MIN_LENGTH, `must be at least ${AB_TEST_DESC_MIN_LENGTH} characters long`),
	flowOrScreensImpacted: yup.string().required('Flow or screens impacted is a required field'),
	start: yup
		.number()
		.transform((value, originalValue) => {
			if (originalValue === EMPTY_STRING || originalValue === null) {
				return undefined;
			}
			return originalValue;
		})
		.test({
			message: 'start date should be valid date',
			test: (value: any) => value !== EMPTY_STRING || value !== null || !Number.isNaN(new Date(value).getTime())
		})
		.test({
			message: 'start date should be greater than current time',
			test: (value: any) => new Date(value) > new Date()
		})
		.required('start date is a required field'),
	end: yup
		.number()
		.transform((value, originalValue) => {
			if (originalValue === EMPTY_STRING || originalValue === null) {
				return undefined;
			}
			return originalValue;
		})
		.test({
			message: 'End date should be valid date',
			test: (value: any) => value !== EMPTY_STRING || value !== null || !Number.isNaN(new Date(value).getTime())
		})
		.test({
			message: 'End date should be greater than current time',
			test: (value: any) => new Date(value) > new Date()
		})
		.required('End date is a required field'),
	controlBucket: yup.object().shape({
		share: yup
			.number()
			.min(0, 'Should be a positive value')
			.max(100, 'Can not be greater than 100')
			.required('Share is a required field'),
		description: yup
			.string()
			.min(AB_TEST_BUCKET_DESC_MIN_LENGTH, BUCKET_DESC_MIN_LENGTH_ERROR_MESSAGE)
			.required('Description is a required field')
	}),
	testBuckets: yup
		.array()
		.of(
			yup.object().shape({
				share: yup
					.number()
					.min(0, 'Should be a positive value')
					.max(100, 'Can not be greater than 100')
					.required('Share is a required field'),
				description: yup
					.string()
					.min(AB_TEST_BUCKET_DESC_MIN_LENGTH, BUCKET_DESC_MIN_LENGTH_ERROR_MESSAGE)
					.required('Description is a required field')
			})
		)
		.test({
			name: 'bucketShareError',
			message: 'Sum of shares should be equal to 100',
			test: (buckets: any, testContext) => {
				const testSum = _sumBy(buckets, 'share');
				const controlBucketShare = _get(testContext, ['parent', 'controlBucket', 'share'], 0);

				return testSum + controlBucketShare === 100;
			}
		}),
	segmentId: yup.string(),
	metrics: yup
		.array()
		.of(yup.string())
		.when('useOldAbFlow', {
			is: false,
			then: yup.array().of(yup.string()).min(1, 'At least one metric is required when not using the Old AB Flow'),
			otherwise: yup.array().of(yup.string())
		}),
	useOldAbFlow: yup.boolean().required('Use Old AB Flow is a required field'),
	recency: yup.number().min(7, 'Recency must be at least 7 days').max(365, 'Recency cannot exceed 365 days'),
	sendAbtestDataOnCT: yup.boolean()
});

export const editValidationSchema = yup.object().shape({
	name: yup.string().required('Name is a required field'),
	description: yup.string().required('Description is a required field').min(10, 'must be at least 10 characters long'),
	flowOrScreensImpacted: yup.string(),
	end: yup
		.number()
		.transform((value, originalValue) => {
			if (originalValue === EMPTY_STRING || originalValue === null) {
				return undefined;
			}
			return originalValue;
		})
		.test({
			message: 'End date should be valid date',
			test: (value: any) => value !== EMPTY_STRING || value !== null || !Number.isNaN(new Date(value).getTime())
		})
		.test({
			message: 'End date should be greater than current time',
			test: (value: any) => new Date(value) > new Date()
		})
		.required('End date is a required field'),
	controlBucket: yup.object().shape({
		share: yup
			.number()
			.min(0, 'Should be a positive value')
			.max(100, 'Can not be greater than 100')
			.required('Share is a required field')
	}),
	testBuckets: yup
		.array()
		.of(
			yup.object().shape({
				share: yup
					.number()
					.min(0, 'Should be a positive value')
					.max(100, 'Can not be greater than 100')
					.required('Share is a required field')
			})
		)
		.test({
			name: 'bucketShareError',
			message: 'Sum of shares should be equal to 100',
			test: (buckets: any, testContext) => {
				const testSum = _sumBy(buckets, 'share');
				const controlBucketShare = _get(testContext, ['parent', 'controlBucket', 'share'], 0);

				return testSum + controlBucketShare === TOTAL_BUCKET_SHARE;
			}
		}),
	bucketTransitionData: yup.array().of(
		yup.object().shape({
			from: yup.string().required('Update From is required'),
			to: yup
				.string()
				.required('Update To is required')
				.test({
					name: 'not-same-as-from',
					message: 'Update To cannot be the same as Update From',
					test(value) {
						const { from } = this.parent;
						return value !== from;
					}
				}),
			percentage: yup.number().required('Update Percentage is required')
		})
	)
});

export const createFormFieldConfig = (fetchSelectOptions: any, isEdit: boolean = false) => {
	return {
		[FORM_FIELD_KEYS.NAME]: new RenderConfig()
			.setName('name')
			.setLabel('Name')
			.setRenderer(TextInput)
			.setRenderProps({ isEditable: { create: true, edit: false }, required: true }),
		[FORM_FIELD_KEYS.DESC]: new RenderConfig()
			.setName('description')
			.setLabel('Description')
			.setRenderer(TextInput)
			.setRenderProps({ isEditable: { create: true, edit: true }, required: true }),
		[FORM_FIELD_KEYS.FLOW_OR_SCREENS_IMPACTED]: new RenderConfig()
			.setName('flowOrScreensImpacted')
			.setLabel('Flow or Screens Impacted')
			.setRenderer(TextInput)
			.setRenderProps({ isEditable: { create: true, edit: false }, required: true }),
		[FORM_FIELD_KEYS.START]: new RenderConfig()
			.setName('start')
			.setLabel('Start Date')
			.setRenderer(TextInput)
			.setRenderProps({
				isEditable: { create: true, edit: true, required: true },
				inputType: 'datetime-local',
				dependencies: ['start', 'end'],
				dependencyResolver: startDateDependencyResolver
			}),
		[FORM_FIELD_KEYS.END]: new RenderConfig()
			.setName('end')
			.setLabel('End Date')
			.setRenderer(TextInput)
			.setRenderProps({
				isEditable: { create: true, edit: true, required: true },
				inputType: 'datetime-local'
			}),
		[FORM_FIELD_KEYS.TEST_BUCKETS]: new RenderConfig()
			.setName('testBuckets')
			.setLabel('Test Buckets')
			.setRenderProps({ isEditable: { create: true, edit: false }, isArrayField: true, required: true, isDisabled: true }),
		[FORM_FIELD_KEYS.CONTROL_BUCKET]: new RenderConfig()
			.setName('controlBucket')
			.setLabel('Control Bucket')
			.setRenderProps({ isEditable: { create: true, edit: false }, required: true, isDisabled: true }),
		[FORM_FIELD_KEYS.BUCKET_NAME]: new RenderConfig()
			.setName('name')
			.setLabel('Bucket Name')
			.setRenderer(TextInput)
			.setRenderProps({ isEditable: { create: true, edit: false } }),
		[FORM_FIELD_KEYS.BUCKET_DESC]: new RenderConfig()
			.setName('description')
			.setLabel('Bucket Description')
			.setRenderer(TextInput)
			.setRenderProps({ isEditable: { create: true, edit: false } }),
		[FORM_FIELD_KEYS.BUCKET_SHARE]: new RenderConfig()
			.setName('share')
			.setLabel('Bucket Percentage')
			.setRenderer(NumberInput)
			.setRenderProps({ isEditable: { create: true, edit: true }, required: true }),
		[FORM_FIELD_KEYS.CONTROL_BUCKET_SHARE]: new RenderConfig()
			.setName('share')
			.setLabel('Bucket Percentage')
			.setRenderer(NumberInput)
			.setRenderProps({
				isEditable: { create: true, edit: false },
				dependencyResolver: controlShareDependencyResolver,
				required: true
			}),
		[FORM_FIELD_KEYS.IS_ALLOWED]: new RenderConfig()
			.setName('isAllowed')
			.setLabel('Is Allowed')
			.setRenderer(SelectInput)
			.setRenderProps({
				isEditable: { create: false, edit: true },
				required: false,
				options: BOOLEAN_OPTIONS,
				dependencies: ['useOldAbFlow'],
				dependencyResolver: (props: any, dependencyValues: any) => {
					const useOldAbFlowValue = _get(dependencyValues, '0', false);

					const { name, setValue } = props;

					if (!useOldAbFlowValue) {
						setValue(name, undefined);
					}

					return {
						disabled: !useOldAbFlowValue && !isEdit
					};
				}
			}),
		[FORM_FIELD_KEYS.SEGMENT_ID]: new RenderConfig()
			.setName('segmentId')
			.setLabel('Segment ID')
			.setRenderer(SearchSelectInput)
			.setRenderProps({
				isEditable: { create: true, edit: false },
				required: false,
				fetchOptions: fetchSelectOptions
			}),
		[FORM_FIELD_KEYS.METRICS]: new RenderConfig()
			.setName('metrics')
			.setLabel('Metrics')
			.setRenderer(SelectInput)
			.setRenderProps({
				isEditable: { create: true, edit: false },
				required: false,
				options: METRICS_OPTIONS,
				multi: true,
				dependencies: ['useOldAbFlow'],
				dependencyResolver: (_: any, dependencyValues: any) => {
					const useOldAbFlowValue = _get(dependencyValues, '0', false);
					return { required: !useOldAbFlowValue };
				}
			}),
		[FORM_FIELD_KEYS.BUCKET_TRANSITION_DATA]: new RenderConfig()
			.setName('bucketTransitionData')
			.setLabel('Bucket Transition Data')
			.setRenderProps({
				isEditable: { create: false, edit: true },
				isArrayField: true,
				dependencies: ['useOldAbFlow'],
				dependencyResolver: (_: any, dependencyValues: any) => {
					const useOldAbFlowValue = _get(dependencyValues, '0', false);

					return {
						isEditable: { create: false, edit: !useOldAbFlowValue },
						required: !useOldAbFlowValue,
						isArrayField: true
					};
				}
			}),
		[FORM_FIELD_KEYS.FROM]: new RenderConfig()
			.setName('from')
			.setLabel('Update From')
			.setRenderer(AsyncSelect)
			.setRenderProps({
				isEditable: { create: true, edit: true, required: true }
			}),
		[FORM_FIELD_KEYS.TO]: new RenderConfig()
			.setName('to')
			.setLabel('Update To')
			.setRenderer(AsyncSelect)
			.setRenderProps({
				isEditable: { create: true, edit: true, required: true }
			}),
		[FORM_FIELD_KEYS.PERCENTAGE]: new RenderConfig()
			.setName('percentage')
			.setLabel('Update Percentage')
			.setRenderer(NumberInput)
			.setRenderProps({ isEditable: { create: false, edit: true }, required: false }),
		[FORM_FIELD_KEYS.USE_OLD_AB_FLOW]: new RenderConfig()
			.setName('useOldAbFlow')
			.setLabel('Use Old AB Flow')
			.setRenderer(SelectInput)
			.setRenderProps({
				isEditable: { create: true, edit: false },
				required: true,
				options: BOOLEAN_OPTIONS
			}),
		[FORM_FIELD_KEYS.RECENCY]: new RenderConfig()
			.setName('recency')
			.setLabel('Recency (days)')
			.setRenderer(NumberInput)
			.setRenderProps({
				isEditable: { create: true, edit: false },
				required: !isEdit,
				min: 0,
				max: 365,
				dependencies: ['useOldAbFlow'],
				dependencyResolver: (props: any, dependencyValues: any) => {
					const useOldAbFlowValue = _get(dependencyValues, '0', false);

					const { name, setValue } = props;

					if (useOldAbFlowValue) {
						setValue(name, undefined);
					}

					return { disabled: useOldAbFlowValue };
				}
			}),
		[FORM_FIELD_KEYS.SEND_AB_TEST_DATA_ON_CT]: new RenderConfig()
			.setName('sendAbtestDataOnCT')
			.setLabel('Send AB Test Data On CT')
			.setRenderer(SelectInput)
			.setRenderProps({
				isEditable: { create: true, edit: false },
				required: !isEdit,
				options: BOOLEAN_OPTIONS,
				dependencies: ['useOldAbFlow'],
				dependencyResolver: (props: any, dependencyValues: any) => {
					const useOldAbFlowValue = _get(dependencyValues, '0', false);
					const { name, setValue } = props;

					if (useOldAbFlowValue) {
						setValue(name, undefined);
					}

					return { disabled: useOldAbFlowValue };
				}
			})
	};
};

export const FORM_FIELD_CONFIG = createFormFieldConfig(null, true);

export const FORM_LAYOUT_CONFIG = [
	new Row(FORM_FIELD_KEYS.NAME),
	new Row(FORM_FIELD_KEYS.DESC),
	new Row(FORM_FIELD_KEYS.FLOW_OR_SCREENS_IMPACTED),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.START)]), new Column([new Row(FORM_FIELD_KEYS.END)])]),
	new Row(FORM_FIELD_KEYS.TEST_BUCKETS, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.BUCKET_NAME)]),
		new Column([new Row(FORM_FIELD_KEYS.BUCKET_DESC)]),
		new Column([new Row(FORM_FIELD_KEYS.BUCKET_SHARE)])
	]),
	new Row(FORM_FIELD_KEYS.CONTROL_BUCKET, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.BUCKET_NAME)]),
		new Column([new Row(FORM_FIELD_KEYS.BUCKET_DESC)]),
		new Column([new Row(FORM_FIELD_KEYS.CONTROL_BUCKET_SHARE)])
	]),
	new Row(FORM_FIELD_KEYS.USE_OLD_AB_FLOW),
	new Row(FORM_FIELD_KEYS.IS_ALLOWED),
	new Row(FORM_FIELD_KEYS.SEGMENT_ID),
	new Row(FORM_FIELD_KEYS.METRICS),
	new Row(FORM_FIELD_KEYS.BUCKET_TRANSITION_DATA, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.FROM)]),
		new Column([new Row(FORM_FIELD_KEYS.TO)]),
		new Column([new Row(FORM_FIELD_KEYS.PERCENTAGE)])
	]),
	new Row(FORM_FIELD_KEYS.RECENCY),
	new Row(FORM_FIELD_KEYS.SEND_AB_TEST_DATA_ON_CT)
];
